import { useMemo } from "react";
import {
  type ActionOutcome,
  type ConfirmQuery,
  defineAction,
  runAction,
} from "./defineAction";
import { dispatch, scopedKey } from "./store";
import { useActionSnapshot } from "./useAction";
import { isPendingStatus } from "./types";
import { toast } from "sonner";

/**
 * The adapter for an ORDINARY mutation — a rename, a move, a reorder, a type
 * change. Anything with no run row behind it.
 *
 * `use-summary-resource.ts` is the equivalent for worker-backed work. The two
 * differ only in how they learn the work is done: that one watches a server
 * run and fences on its identity, this one owns a read it issues after the
 * mutation resolved. Everything else — pending held outside React, keyed per
 * target, released by the store — is the same layer.
 *
 * What this replaces at a call site:
 *
 * - `mutation.isPending`, which is shared across every row a mutation serves,
 *   so one row's save greys out the rest.
 * - `onMutate` snapshot + `onError` rollback, which restores whatever was in
 *   the cache when the snapshot was taken — including another attempt's
 *   optimism, and including data the server has since rejected.
 * - a keyed `useState` set per action, which dies on unmount.
 */
export interface MutationActionOptions<TInput, TValue> {
  /** Namespaces the store. Distinct per action, shared across targets. */
  name: string;
  /**
   * Stable per-target key — the row id, or `${rowId}:${field}` where one row
   * has several independently-savable things on it. Two controls sharing a
   * key means one shows the other's spinner.
   */
  key: (input: TInput) => string;
  /** Perform the write. Usually the tRPC mutation's `mutateAsync`. */
  mutate: (input: TInput) => Promise<unknown>;
  /**
   * The exact query entries this write becomes visible in — procedure AND
   * input. Build them with `confirmQuery`, which owns the cancel-then-fetch
   * rule that makes the confirming read provably post-mutation.
   */
  confirmWith: (input: TInput) => readonly ConfirmQuery[];
  /**
   * What to show while the write is outstanding. Assert only what the user
   * supplied — the text they typed, the option they picked. Never a
   * server-derived value; show the previous one or a placeholder instead.
   */
  optimistic?: (input: TInput) => TValue;
  /**
   * `"drop"` (default) ignores a press while one is outstanding — right for a
   * button. `"serialize"` queues it — right for something a user can retrigger
   * quickly and legitimately, like dragging a row twice.
   */
  concurrency?: "drop" | "serialize";
  /**
   * Prefix for the failure toast this adapter raises — "Rename", "Move".
   *
   * Reporting lives HERE rather than on the mutation because only the layer
   * knows which kind of failure it was. A mutation's `onError` sees a bare
   * rejection and calls everything a failure; the layer can tell a refusal
   * ("the server said no", safe to retry) from a lost response ("we never
   * found out", NOT safe to retry). Two reporters means the confident wrong
   * message lands next to the accurate uncertain one.
   */
  errorLabel?: string;
  /** Inline callers must await `run` and display refusals AND unknown outcomes. */
  errorPresentation?: "toast" | "inline";
  onError?: (message: string) => void;
}

export interface MutationAction<TInput, TValue> {
  /**
   * Start the write. Resolves when the attempt settles, with what happened.
   *
   * It never rejects — most presses are fire-and-forget, and a rejecting
   * promise would make each of those an unhandled rejection. **A caller that
   * awaits this MUST check `ok`**: treating a settled promise as success is
   * how a refused write ends up with a success toast.
   */
  run: (input: TInput) => Promise<ActionOutcome>;
  /** True while a write for this target is outstanding. */
  isPending: (targetKey: string) => boolean;
  /**
   * True when a write's outcome was never observed. It may have applied, so
   * the control must say so rather than looking untouched — and the layer will
   * not resend for this target until the user decides.
   */
  isIndeterminate: (targetKey: string) => boolean;
  /**
   * Dismiss a settled outcome for a target, returning the control to idle.
   *
   * The one that matters is `unknown`: it is deliberately sticky so the user
   * sees that a write may have applied, and this is how they say "understood".
   * Pressing the control again does this implicitly — the layer treats an
   * explicit press as the decision to retry.
   */
  acknowledge: (targetKey: string) => void;
  /** The value an outstanding write is asserting, to merge over query data. */
  optimisticFor: (targetKey: string) => TValue | undefined;
  /**
   * Every outstanding optimistic value for this action. For optimism that is
   * about a GROUP rather than a row — a drag order — where there is no row key
   * to look it up by. Pass the scope prefix where the key is only unique
   * within one (a reorder group repeats in every round).
   */
  pendingValues: (targetPrefix?: string) => TValue[];
}

/**
 * Await a press and throw if it did not land.
 *
 * For callers whose control flow was built around a rejecting `mutateAsync`
 * and still depends on it — an editor that must stay open on failure, an
 * ordered chain whose later steps are only valid if the earlier ones applied.
 */
export async function orThrow(press: Promise<ActionOutcome>): Promise<void> {
  const outcome = await press;

  if (outcome.ok) {
    return;
  }

  if (outcome.error) {
    throw new Error(outcome.error);
  }

  throw new Error(
    outcome.reason === "dropped"
      ? "Another change to this is still saving. Try again in a moment."
      : outcome.reason === "gated"
        ? "Busy starting other jobs — try that again in a moment."
        : "That change did not go through.",
  );
}

export function useMutationAction<TInput, TValue = unknown>(
  options: MutationActionOptions<TInput, TValue>,
): MutationAction<TInput, TValue> {
  const {
    name,
    key,
    mutate,
    confirmWith,
    optimistic,
    concurrency,
    errorLabel,
    errorPresentation = "toast",
    onError,
  } = options;

  const snapshot = useActionSnapshot();

  const action = useMemo(
    () =>
      defineAction<TInput>({
        name,
        confirm: "queries",
        key,
        run: mutate,
        confirmWith,
        optimistic,
        concurrency,
        onError,
      }),
    [name, key, mutate, confirmWith, optimistic, concurrency, onError],
  );

  return useMemo(
    () => ({
      run: async (input: TInput) => {
        const outcome = await runAction(action, input);

        if (errorPresentation === "inline") {
          return outcome;
        }

        // Raised HERE rather than left to the caller: most presses are
        // `void action.run(...)` and discard the outcome, so a caller-side
        // check would tell nobody. An unknown outcome has to be said out loud
        // — it is the one case where the user has to decide whether to retry,
        // and the mutation's own error toast would call it a failure, which
        // is a different and more comforting claim than the truth.
        const label = errorLabel ? `${errorLabel}: ` : "";

        if (outcome.reason === "failed") {
          toast.error(`${label}${outcome.error ?? "the server refused it."}`);
        }

        if (outcome.reason === "unknown") {
          toast.warning(
            `${label}we lost contact with the server, so this may or may not have saved. Reload to check before trying again.`,
          );
        }

        // NOT `dropped`, which is a double-click and correctly silent. This is
        // the case where the control looked available and the press did
        // nothing at all.
        if (outcome.reason === "gated") {
          toast.info("Busy starting other jobs — try that again in a moment.");
        }

        return outcome;
      },
      isPending: (targetKey: string) => {
        const attempt = snapshot.attempts.get(scopedKey(name, targetKey));
        return attempt ? isPendingStatus(attempt.status) : false;
      },
      isIndeterminate: (targetKey: string) =>
        snapshot.attempts.get(scopedKey(name, targetKey))?.status === "unknown",
      acknowledge: (targetKey: string) =>
        dispatch(scopedKey(name, targetKey), { type: "ACKNOWLEDGE" }),
      optimisticFor: (targetKey: string) =>
        snapshot.optimistic.get(scopedKey(name, targetKey)) as
          | TValue
          | undefined,
      pendingValues: (targetPrefix?: string) =>
        [...snapshot.optimistic]
          .filter(([key]) => key.includes(`::${name}:${targetPrefix ?? ""}`))
          .map(([, value]) => value as TValue),
    }),
    [action, name, errorLabel, errorPresentation, snapshot],
  );
}

/** A single target's execution and presentation, derived from the same input. */
export interface BoundMutationAction {
  run: () => Promise<ActionOutcome>;
  isPending: boolean;
}

export function useBoundMutationAction<TInput>(
  options: MutationActionOptions<TInput, unknown>,
  input: TInput,
): BoundMutationAction {
  const action = useMutationAction(options);
  return {
    run: () => action.run(input),
    isPending: action.isPending(options.key(input)),
  };
}
